import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client.js';
import type {
  RingGroup, Queue, IvrMenu, Schedule, PhoneNumber, InboundRoute, FeatureCodes, ParkingLot,
} from '@arcnet/contracts';

interface ListResponse<T> { data: T[] }

export const tmsKeys = {
  ringGroups: (t: string) => ['ring-groups', t] as const,
  queues: (t: string) => ['queues', t] as const,
  ivrs: (t: string) => ['ivrs', t] as const,
  schedules: (t: string) => ['schedules', t] as const,
  numbers: (t: string) => ['numbers', t] as const,
  inboundRoutes: (t: string) => ['inbound-routes', t] as const,
  featureCodes: (t: string) => ['tenants', t, 'feature-codes'] as const,
  businessHours: (t: string) => ['tenants', t, 'business-hours'] as const,
  conferenceRooms: (t: string) => ['conference-rooms', t] as const,
  parkingLots: (t: string) => ['parking-lots', t] as const,
  users: (t: string) => ['tenant-users', t] as const,
  grantableRoles: (t: string) => ['grantable-roles', t] as const,
};

const list = <T,>(key: readonly unknown[], url: string, enabled: boolean) =>
  useQuery({ queryKey: key, queryFn: () => api.get<ListResponse<T>>(url), enabled });

export const useRingGroups = (t: string) =>
  list<RingGroup>(tmsKeys.ringGroups(t), `/v1/tenants/${t}/ring-groups`, !!t);
export const useQueues = (t: string) =>
  list<Queue>(tmsKeys.queues(t), `/v1/tenants/${t}/queues`, !!t);
export const useIvrs = (t: string) =>
  list<IvrMenu>(tmsKeys.ivrs(t), `/v1/tenants/${t}/ivrs`, !!t);
export const useSchedules = (t: string) =>
  list<Schedule>(tmsKeys.schedules(t), `/v1/tenants/${t}/schedules`, !!t);
export interface ConferenceRoom {
  id: string; tenant_id: string; name: string; extension: string | null;
  pin: string | null; moderator_pin: string | null;
  wait_for_moderator: boolean; end_with_moderator: boolean;
  announce_join: boolean; announce_leave: boolean;
  moh_when_alone: boolean; record_room: boolean;
  max_members: number; profile: string;
  greeting_media_id: string | null; created_at: string;
}

export const useConferenceRooms = (t: string) =>
  list<ConferenceRoom>(tmsKeys.conferenceRooms(t), `/v1/tenants/${t}/conference-rooms`, !!t);

export function useSaveConferenceRoom(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? api.patch(`/v1/tenants/${tenantId}/conference-rooms/${id}`, body)
         : api.post(`/v1/tenants/${tenantId}/conference-rooms`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tmsKeys.conferenceRooms(tenantId) });
    },
  });
}

export function useDeleteConferenceRoom(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete(`/v1/tenants/${tenantId}/conference-rooms/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tmsKeys.conferenceRooms(tenantId) });
    },
  });
}

export const useNumbers = (t: string) =>
  list<PhoneNumber>(tmsKeys.numbers(t), `/v1/tenants/${t}/numbers`, !!t);

export interface TenantUser {
  id: string; email: string; full_name: string | null; status: string;
  last_seen_at: string | null; membership_id: string;
  role_key: string; role_name: string; granted_at: string;
  /** What they reach through the self-service permissions (docs/33 §5). */
  departments: { id: string; name: string }[];
  /** Two-step verification is on (docs/06 §2.1). */
  mfa_enabled: boolean;
  extensions: { id: string; number: string; display_name: string | null }[];
}
export interface PendingInvite {
  id: string; email: string; full_name: string | null;
  role_key: string; role_name: string; expires_at: string; created_at: string;
  /** Seats linked to them when they accept (docs/33 §5). */
  extension_numbers: string[];
  department_names: string[];
  /** Past its expiry: resend it for a new link and seven more days. */
  expired?: boolean;
}

export const useTenantUsers = (t: string) =>
  useQuery({
    queryKey: tmsKeys.users(t),
    queryFn: () => api.get<{ data: TenantUser[]; pending: PendingInvite[] }>(
      `/v1/tenants/${t}/users`),
    enabled: !!t,
  });

export const useGrantableRoles = (t: string) =>
  useQuery({
    queryKey: tmsKeys.grantableRoles(t),
    queryFn: () => api.get<ListResponse<{
      id: string; key: string; name: string; description: string | null;
      owner: 'builtin' | 'platform' | 'reseller' | 'tenant';
    }>>(`/v1/tenants/${t}/grantable-roles`),
    enabled: !!t,
  });

/**
 * Invite a portal user onto this account.
 *
 * The API refuses any role granting a permission the inviter does not hold, so
 * a tenant admin can create TMS users for their own PBX but cannot manufacture
 * a platform admin.
 */
export function useInviteTenantUser(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      email: string; role_key: string; full_name?: string; extension_ids?: string[]; department_ids?: string[];
    }) =>
      api.post<{ token: string; email: string; expires_at: string; emailed?: boolean }>('/v1/invitations', {
        ...body, scope_level: 'tenant', tenant_id: tenantId,
      }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: tmsKeys.users(tenantId) }); },
  });
}

/** Replace somebody's departments and own extensions (docs/33 §5). */
export function useSetUserAccess(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...body }: { userId: string; department_ids: string[]; extension_ids: string[] }) =>
      api.put(`/v1/tenants/${tenantId}/users/${userId}/access`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tmsKeys.users(tenantId) });
      void qc.invalidateQueries({ queryKey: ['extensions', tenantId] });
    },
  });
}

/** A new link for a pending invitation, expired or not, emailed again (the old link stops working). */
export function useResendInvite(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ email: string; token: string; emailed: boolean }>(
      `/v1/invitations/${id}/actions/resend`, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: tmsKeys.users(tenantId) }); },
  });
}

export function useRevokeInvite(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/v1/invitations/${id}/actions/revoke`, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: tmsKeys.users(tenantId) }); },
  });
}

export function useRemoveTenantUser(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.delete<void>(`/v1/tenants/${tenantId}/users/${userId}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: tmsKeys.users(tenantId) }); },
  });
}

export function useSaveRingGroup(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? api.patch(`/v1/tenants/${tenantId}/ring-groups/${id}`, body)
         : api.post(`/v1/tenants/${tenantId}/ring-groups`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: tmsKeys.ringGroups(tenantId) }); },
  });
}

export function useSaveQueue(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? api.patch(`/v1/tenants/${tenantId}/queues/${id}`, body)
         : api.post(`/v1/tenants/${tenantId}/queues`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: tmsKeys.queues(tenantId) }); },
  });
}

export function useSaveIvr(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? api.patch(`/v1/tenants/${tenantId}/ivrs/${id}`, body)
         : api.post(`/v1/tenants/${tenantId}/ivrs`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: tmsKeys.ivrs(tenantId) }); },
  });
}

export function useSaveSchedule(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? api.patch(`/v1/tenants/${tenantId}/schedules/${id}`, body)
         : api.post(`/v1/tenants/${tenantId}/schedules`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: tmsKeys.schedules(tenantId) }); },
  });
}

export function useDeleteTms(
  tenantId: string, resource: 'ring-groups' | 'queues' | 'ivrs' | 'schedules',
) {
  const qc = useQueryClient();
  const key = resource === 'ring-groups' ? tmsKeys.ringGroups(tenantId)
    : resource === 'queues' ? tmsKeys.queues(tenantId)
    : resource === 'schedules' ? tmsKeys.schedules(tenantId)
    : tmsKeys.ivrs(tenantId);
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/v1/tenants/${tenantId}/${resource}/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: key }); },
  });
}

/** Named inbound routes and the numbers on none (docs/26 D1). */
export const useInboundRoutes = (t: string) =>
  useQuery({
    queryKey: tmsKeys.inboundRoutes(t),
    queryFn: () => api.get<{ data: InboundRoute[]; unrouted: Array<{ id: string; e164: string; label?: string | null }> }>(
      `/v1/tenants/${t}/inbound-routes`),
    enabled: !!t,
    // Open or closed now is a clock reading.
    refetchInterval: 60_000,
  });

export function useSaveInboundRoute(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? api.patch<{ id: string }>(`/v1/tenants/${tenantId}/inbound-routes/${id}`, body)
         : api.post<{ id: string }>(`/v1/tenants/${tenantId}/inbound-routes`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tmsKeys.inboundRoutes(tenantId) });
      void qc.invalidateQueries({ queryKey: tmsKeys.numbers(tenantId) });
    },
  });
}

export function useDeleteInboundRoute(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/v1/tenants/${tenantId}/inbound-routes/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tmsKeys.inboundRoutes(tenantId) });
      void qc.invalidateQueries({ queryKey: tmsKeys.numbers(tenantId) });
    },
  });
}

/** The account's own name for a number (0098). */
export function useLabelNumber(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, label }: { id: string; label: string | null }) =>
      api.put(`/v1/tenants/${tenantId}/numbers/${id}/label`, { label }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tmsKeys.numbers(tenantId) });
      void qc.invalidateQueries({ queryKey: tmsKeys.inboundRoutes(tenantId) });
    },
  });
}

/** What the account's phones dial, its own codes over the defaults (docs/26 E1). */
export const useFeatureCodes = (t: string) =>
  useQuery({
    queryKey: tmsKeys.featureCodes(t),
    queryFn: () => api.get<FeatureCodes>(`/v1/tenants/${t}/feature-codes`),
    enabled: !!t,
  });

/** Which schedule is the account's business hours, and whether it is open now. */
export const useBusinessHours = (t: string) =>
  useQuery({
    queryKey: tmsKeys.businessHours(t),
    queryFn: () => api.get<{ schedule_id: string | null; name: string | null; open: boolean; explanation: string }>(
      `/v1/tenants/${t}/business-hours`),
    enabled: !!t,
  });

export function useNominateBusinessHours(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId: string) =>
      api.put(`/v1/tenants/${tenantId}/business-hours/schedule`, { schedule_id: scheduleId }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: tmsKeys.businessHours(tenantId) }); },
  });
}

export const useParkingLots = (t: string) =>
  list<ParkingLot>(tmsKeys.parkingLots(t), `/v1/tenants/${t}/parking-lots`, !!t);

export function useSaveParkingLot(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? api.patch(`/v1/tenants/${tenantId}/parking-lots/${id}`, body)
         : api.post(`/v1/tenants/${tenantId}/parking-lots`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: tmsKeys.parkingLots(tenantId) }); },
  });
}

export function useDeleteParkingLot(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/v1/tenants/${tenantId}/parking-lots/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: tmsKeys.parkingLots(tenantId) }); },
  });
}
